import { DialogRef } from '@angular/cdk/dialog';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, Input, TemplateRef, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { ValidationService } from '@app/forms/validation.service';
import { AutofocusDirective } from '@sb/shared/util';
import { DialogConfirmResult, SbDialogModule, SbDialogService, type DialogConfirmComponent } from '@sb/ui/dialog';
import { SbFormFieldComponent } from '@sb/ui/form-field';
import { InputDirective } from '@sb/ui/input';
import { Observable, startWith, takeUntil } from 'rxjs';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-dialog-confirm-high-impact',
  imports: [ReactiveFormsModule, SbFormFieldComponent, InputDirective, AutofocusDirective, SbDialogModule],
  templateUrl: './dialog-confirm-high-impact.component.html',
})
export class DialogConfirmHighImpactComponent {
  private dialog = inject(SbDialogService);
  private destroyRef = inject(DestroyRef);

  @ViewChild('dialogContent', { static: false })
  private dialogContent!: TemplateRef<unknown>;

  @Input({ required: true })
  title: string;

  @Input({ required: true })
  confirmButtonLabel: string;

  @Input({ required: true })
  matchLabel: string;

  @Input({ required: true })
  matchError: string;

  @Input({ required: true })
  matchValue: string;

  private dialogRef: DialogRef<DialogConfirmResult, DialogConfirmComponent> | undefined;

  formControl = new FormControl('', [Validators.required]);

  openDialog(): Observable<DialogConfirmResult> {
    this.resetForm();

    this.dialogRef = this.dialog.openConfirm({
      type: 'danger',
      primary: { text: this.confirmButtonLabel },
      title: this.title,
      contentTemplate: this.dialogContent,
    });

    this.watchFormControlStatus();

    return this.dialogRef.closed;
  }

  closeDialog() {
    this.dialogRef?.close();
    this.dialogRef = undefined;
  }

  private watchFormControlStatus() {
    void this.formControl.statusChanges
      .pipe(startWith(this.formControl.status), takeUntil(this.dialogRef.closed), takeUntilDestroyed(this.destroyRef))
      .subscribe((status) => {
        this.updatePrimaryButtonDisabled(status === 'INVALID');
      });
  }

  private updatePrimaryButtonDisabled(disabled: boolean) {
    if (!this.dialogRef) {
      return;
    }
    this.dialogRef.componentInstance.data.primary.disabled = disabled;
  }

  private resetForm() {
    this.formControl.reset();
    this.formControl.setValidators([
      Validators.required,
      ValidationService.matchText(this.matchValue, this.matchError),
    ]);
    this.formControl.updateValueAndValidity();
  }
}
